import type { TeamId } from '@/core/schemas';

import type { StandingsRow } from '../../contracts';
import type { DecidedMatch } from '../../matchResults';

import type { GroupStageConfig, Tiebreaker } from './config';

interface Tally {
  teamId: TeamId;
  played: number;
  won: number;
  drawn: number;
  lost: number;
  points: number;
  legsFor: number;
  legsAgainst: number;
  setsFor: number;
  setsAgainst: number;
}

function emptyTally(teamId: TeamId): Tally {
  return {
    teamId,
    played: 0,
    won: 0,
    drawn: 0,
    lost: 0,
    points: 0,
    legsFor: 0,
    legsAgainst: 0,
    setsFor: 0,
    setsAgainst: 0,
  };
}

function applyResult(tally: Tally, isHome: boolean, match: DecidedMatch, config: GroupStageConfig) {
  const own = isHome ? match.legs.home : match.legs.away;
  const other = isHome ? match.legs.away : match.legs.home;

  tally.played += 1;
  tally.legsFor += own;
  tally.legsAgainst += other;

  if (match.sets !== null) {
    tally.setsFor += isHome ? match.sets.home : match.sets.away;
    tally.setsAgainst += isHome ? match.sets.away : match.sets.home;
  }

  if (match.winnerTeamId === null) {
    tally.drawn += 1;
    tally.points += config.pointsForDraw;
    return;
  }

  if (match.winnerTeamId === tally.teamId) {
    tally.won += 1;
    tally.points += config.pointsForWin;
  } else {
    tally.lost += 1;
    tally.points += config.pointsForLoss;
  }
}

/** Points a team took from the matches against one specific opponent. */
function headToHeadPoints(
  teamId: TeamId,
  opponentId: TeamId,
  matches: readonly DecidedMatch[],
  config: GroupStageConfig,
): number {
  let points = 0;

  for (const match of matches) {
    const involvesBoth =
      (match.homeTeamId === teamId && match.awayTeamId === opponentId) ||
      (match.homeTeamId === opponentId && match.awayTeamId === teamId);

    if (!involvesBoth) {
      continue;
    }

    if (match.winnerTeamId === null) {
      points += config.pointsForDraw;
    } else if (match.winnerTeamId === teamId) {
      points += config.pointsForWin;
    } else {
      points += config.pointsForLoss;
    }
  }

  return points;
}

type Comparator = (a: StandingsRow, b: StandingsRow) => number;

function comparatorFor(
  tiebreaker: Tiebreaker,
  matches: readonly DecidedMatch[],
  config: GroupStageConfig,
): Comparator {
  switch (tiebreaker) {
    case 'points':
      return (a, b) => b.points - a.points;
    case 'wins':
      return (a, b) => b.won - a.won;
    case 'legDifference':
      return (a, b) => b.legDifference - a.legDifference;
    case 'legsFor':
      return (a, b) => b.legsFor - a.legsFor;
    case 'setDifference':
      return (a, b) => b.setsFor - b.setsAgainst - (a.setsFor - a.setsAgainst);
    case 'headToHead':
      return (a, b) =>
        headToHeadPoints(b.teamId, a.teamId, matches, config) -
        headToHeadPoints(a.teamId, b.teamId, matches, config);
  }
}

/**
 * Builds the table for one group.
 *
 * Ranking follows the configured tiebreakers in order; the team id decides last
 * so the order is stable and never depends on input order. Head-to-head is
 * compared pairwise — with three or more teams on equal points a full mini
 * table would be stricter, which is noted in the documentation.
 */
export function buildStandings(
  teamIds: readonly TeamId[],
  matches: readonly DecidedMatch[],
  config: GroupStageConfig,
): StandingsRow[] {
  const tallies = new Map<TeamId, Tally>(teamIds.map((teamId) => [teamId, emptyTally(teamId)]));

  for (const match of matches) {
    const home = tallies.get(match.homeTeamId);
    const away = tallies.get(match.awayTeamId);

    if (home === undefined || away === undefined) {
      continue; // Match belongs to another group.
    }

    applyResult(home, true, match, config);
    applyResult(away, false, match, config);
  }

  const rows: StandingsRow[] = [...tallies.values()].map((tally) => ({
    ...tally,
    rank: 0,
    legDifference: tally.legsFor - tally.legsAgainst,
  }));

  const comparators = config.tiebreakers.map((tiebreaker) =>
    comparatorFor(tiebreaker, matches, config),
  );

  rows.sort((a, b) => {
    for (const compare of comparators) {
      const result = compare(a, b);
      if (result !== 0) {
        return result;
      }
    }
    return a.teamId.localeCompare(b.teamId);
  });

  return rows.map((row, index) => ({ ...row, rank: index + 1 }));
}
